/**
 * src/api/onboard.ts
 *
 * Client for the Onboard pipeline routes:
 *   • Stage 3: generate-zip (B3)
 *   • Stage 4: open-pr      (B4)
 * B5 will add an end-to-end orchestrator.
 */

import { getApiKey, getBaseUrl } from './client';

export type PageType = 'html' | 'json';

/** JSON-API specific fields. Required when pageType === 'json'. */
export interface JsonGenerateFields {
  listPath:        string;
  titleKey:        string;
  priceKey:        string;
  urlKey:          string;
  idKey:           string;
  paginationParam?: string;
  paginationType?:  'page' | 'offset' | 'cursor';
}

/** HTML-via-CF-Worker generator fields. Mirrors the backend
 *  `HtmlGenerateFields` interface. Required when `pageType === 'html'`
 *  AND `recommendedEngine === 'cf_http'`; ignored for `docker_*`. */
export interface HtmlGenerateFields {
  listingSelector:  string;
  titleSelector:    string;
  priceSelector:    string;
  urlSelector:      string;
  idSelector:       string;
  paginationParam?: string;
  paginationType?:  'page' | 'offset' | 'cursor';
  paginationBase?:  number;
  paginationMultiplier?: number;
}

export interface GenerateRequest {
  sourceId:           string;
  displayName:        string;
  urlPattern:         string;
  listingSelectors:   string[];
  humanevasionTier:   'js' | 'cdp' | 'vm';
  defaultSearchTerms: string[];
  recommendedEngine:  string;
  defaultMaxPage?:    number;
  /** Page type — drives template selection. Defaults 'html'. */
  pageType?:          PageType;
  /** Required when pageType === 'json'. */
  json?:              JsonGenerateFields;
  /** Required when pageType === 'html' AND recommendedEngine === 'cf_http'. */
  html?:              HtmlGenerateFields;
}

// ── B4 — Open PR ─────────────────────────────────────────────────

export interface FeasibilityRow {
  engine:  string;
  ok:      boolean;
  status?: number;
  ms?:     number;
  score:   number;
  note?:   string;
}

export interface ProfileSummary {
  completeness:           number;
  /** 'html' (default) or 'json' — controls which row set the PR
   *  body emphasises. */
  pageType?:              PageType;
  listingSelector?:       string;
  cookieBannerProvider?:  string;
  cookieAcceptSelector?:  string;
  jsRenderRequired:       boolean;
  paginationType?:        string;
  paginationParam?:       string;
  /** Populated when pageType === 'json'. */
  jsonListPath?:          string;
  jsonItemCount?:         number;
  jsonTitleKey?:          string;
  jsonPriceKey?:          string;
  jsonUrlKey?:            string;
  jsonIdKey?:             string;
}

export interface OpenPRRequest extends GenerateRequest {
  feasibilityMatrix?: FeasibilityRow[];
  profileSummary?:    ProfileSummary;
  targetUrl?:         string;
  note?:              string;
}

export interface OpenPRResult {
  ok:        true;
  number:    number;
  url:       string;
  branch:    string;
  commitSha: string;
  files:     string[];
}

// ── Stage 5 — PR-state poll + Register ───────────────────────────

export interface PrState {
  ok:         true;
  number:     number;
  merged:     boolean;
  mergedAt:   string | null;
  baseBranch: string;
  state:      'open' | 'closed';
}

export interface RegisterScraperRequest {
  sourceId:           string;
  displayName:        string;
  pageType:           PageType;
  recommendedEngine:  string;
  prNumber:           number;
  runsPerDayOverride?: number;
  /** Optional onboard_runs id — backend flips pr.registeredAt on
   *  the originating run so the Stage-5 history panel can render
   *  a "registered" badge without a follow-up poll. */
  runId?:             string;
}

export interface RegisterScraperResult {
  ok:           true;
  id:           string;
  displayName:  string;
  executor:     string;
  runsPerDay:   number;
  prNumber:     number;
  prMergedAt:   string;
}

/**
 * Generate a scraper zip and trigger a browser download. The
 * /onboard/generate route is x-api-key gated; we fetch from JS
 * with the header, then `URL.createObjectURL` the response blob
 * + click a hidden anchor — same auth-friendly bridge the
 * Diagnose tab uses for screenshot streaming.
 */
export async function generateAndDownloadScraper(req: GenerateRequest): Promise<void> {
  const apiKey = getApiKey() ?? '';
  const base   = getBaseUrl();
  const resp   = await fetch(`${base}/v1/observe/onboard/generate`, {
    method:  'POST',
    headers: {
      'content-type': 'application/json',
      'x-api-key':    apiKey,
    },
    body:    JSON.stringify(req),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`generate failed: HTTP ${resp.status}: ${txt.slice(0, 200)}`);
  }
  const blob = await resp.blob();
  const url  = URL.createObjectURL(blob);
  // Filename mirrors the Content-Disposition the server set, but
  // we name our anchor based on the sourceId in case the header
  // gets stripped by an intermediate proxy.
  const a = document.createElement('a');
  a.href     = url;
  a.download = `${req.sourceId}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Free the object URL on the next tick so the click handler has
  // time to start the download stream before revocation.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Open an automated PR on the configured monorepo containing the
 * generated scraper. Server-side handles the actual GitHub call —
 * this is just a thin POST wrapper. On 4xx/5xx we surface the
 * server's `message` so the operator sees e.g. "GITHUB_TOKEN env
 * not set" rather than a generic "fetch failed".
 */
export async function openOnboardingPR(req: OpenPRRequest): Promise<OpenPRResult> {
  const apiKey = getApiKey() ?? '';
  const base   = getBaseUrl();
  const resp   = await fetch(`${base}/v1/observe/onboard/open-pr`, {
    method:  'POST',
    headers: {
      'content-type': 'application/json',
      'x-api-key':    apiKey,
    },
    body:    JSON.stringify(req),
  });
  const json = await resp.json().catch(() => ({} as any));
  if (!resp.ok) {
    const msg = json?.message || `HTTP ${resp.status}`;
    throw new Error(msg);
  }
  return json as OpenPRResult;
}

/** Stage 5 — poll the merged state of the Stage-4 PR. The Stage 5
 *  button stays locked until this returns merged=true. Server-side
 *  also enforces this on the register call so a stale frontend can't
 *  bypass the gate. */
export async function getOnboardingPRState(prNumber: number): Promise<PrState> {
  const apiKey = getApiKey() ?? '';
  const base   = getBaseUrl();
  const resp = await fetch(`${base}/v1/observe/onboard/pr-state?number=${encodeURIComponent(String(prNumber))}`, {
    headers: { 'x-api-key': apiKey },
  });
  const json = await resp.json().catch(() => ({} as any));
  if (!resp.ok) {
    throw new Error(json?.message || `HTTP ${resp.status}`);
  }
  return json as PrState;
}

/** Stage 5 — register the new scraper into scrapers_registry with
 *  the derived runs_per_day cadence. Server refuses with HTTP 409 +
 *  error='pr_not_merged' if the referenced PR isn't merged yet. */
export class PrNotMergedError extends Error {
  constructor(message: string) { super(message); this.name = 'PrNotMergedError'; }
}

export async function registerOnboardedScraper(req: RegisterScraperRequest): Promise<RegisterScraperResult> {
  const apiKey = getApiKey() ?? '';
  const base   = getBaseUrl();
  const resp = await fetch(`${base}/v1/observe/onboard/register`, {
    method:  'POST',
    headers: { 'content-type': 'application/json', 'x-api-key': apiKey },
    body:    JSON.stringify(req),
  });
  const json = await resp.json().catch(() => ({} as any));
  if (!resp.ok) {
    if (json?.error === 'pr_not_merged') throw new PrNotMergedError(json.message || 'PR not merged yet');
    throw new Error(json?.message || `HTTP ${resp.status}`);
  }
  return json as RegisterScraperResult;
}

// ── B5 — Run history persistence ────────────────────────────────

export type OnboardStage =
  | 'recon'
  | 'profile'
  | 'generate'
  | 'pr'
  | 'done'
  | 'failed';

export interface OnboardRun {
  id:           string;
  ts:           string;
  updatedAt:    string;
  targetUrl:    string;
  sourceId?:    string;
  displayName?: string;
  stage:        OnboardStage;
  recon?: {
    recommendedEngine: string;
    matrix:            FeasibilityRow[];
  };
  profile?:   ProfileSummary;
  generated?: { files: number };
  pr?: {
    number:    number;
    url:       string;
    branch:    string;
    commitSha: string;
    files:     string[];
    /** Stage-5 merge poll cache. null = not yet polled. */
    merged?:        boolean | null;
    lastCheckedAt?: string  | null;
    /** Set once registerScraper has written scrapers_registry. */
    registeredAt?:  string  | null;
    /** CI rollup for the PR head commit:
     *    'pass' / 'fail' / 'pending' / 'none' / null (unknown). */
    ciStatus?:      'pass' | 'fail' | 'pending' | 'none' | null;
    /** GitHub PR state — 'open' or 'closed'. The backend drops
     *  closed-without-merge rows before they reach the panel, but
     *  surface here too for the rare race between Close-click and
     *  the next poll. */
    state?:         'open' | 'closed' | null;
  };
  error?: { stage: OnboardStage; message: string };
}

export interface OnboardRunSummary {
  id:           string;
  ts:           string;
  updatedAt:    string;
  targetUrl:    string;
  sourceId?:    string;
  displayName?: string;
  stage:        OnboardStage;
  prNumber?:    number;
  prUrl?:       string;
  errorStage?:  OnboardStage;
  errorMsg?:    string;
}

/** POST /v1/observe/onboard/runs — start a new pipeline run. */
export async function createOnboardRun(input: {
  targetUrl: string;
  recon?:    { recommendedEngine: string; matrix: FeasibilityRow[] };
}): Promise<OnboardRun> {
  return jsonPost<OnboardRun>('/v1/observe/onboard/runs', input, 'create_run_failed');
}

/** PATCH /v1/observe/onboard/runs/:id — update a run mid-flight. */
export async function patchOnboardRun(
  id: string,
  patch: {
    stage:        OnboardStage;
    sourceId?:    string;
    displayName?: string;
    profile?:     ProfileSummary;
    generated?:   { files: number };
    pr?:          OpenPRResult;
    error?:       { stage: OnboardStage; message: string };
  },
): Promise<OnboardRun> {
  const apiKey = getApiKey() ?? '';
  const base   = getBaseUrl();
  const resp   = await fetch(`${base}/v1/observe/onboard/runs/${encodeURIComponent(id)}`, {
    method:  'PATCH',
    headers: {
      'content-type': 'application/json',
      'x-api-key':    apiKey,
    },
    body: JSON.stringify(patch),
  });
  const json = await resp.json().catch(() => ({} as any));
  if (!resp.ok) {
    throw new Error(json?.message || `HTTP ${resp.status}`);
  }
  return (json.run ?? json) as OnboardRun;
}

/** GET /v1/observe/onboard/runs — list recent runs (summary rows). */
export async function listOnboardRuns(opts: {
  limit?: number;
  stage?: OnboardStage;
} = {}): Promise<OnboardRunSummary[]> {
  const apiKey = getApiKey() ?? '';
  const base   = getBaseUrl();
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.stage) params.set('stage', opts.stage);
  const qs = params.toString();
  const resp = await fetch(
    `${base}/v1/observe/onboard/runs${qs ? `?${qs}` : ''}`,
    { headers: { 'x-api-key': apiKey } },
  );
  const json = await resp.json().catch(() => ({} as any));
  if (!resp.ok) throw new Error(json?.message || `HTTP ${resp.status}`);
  return (json.runs ?? []) as OnboardRunSummary[];
}

/** GET /v1/observe/onboard/runs/pending-register — Stage-5 history
 *  feed. Server-side polls GitHub for each in-flight pr-stage run
 *  on the way out so the response always carries fresh `merged`
 *  flags (or null if the poll failed / not yet attempted). */
export async function listPendingRegisterRuns(): Promise<OnboardRun[]> {
  const apiKey = getApiKey() ?? '';
  const base   = getBaseUrl();
  const resp = await fetch(
    `${base}/v1/observe/onboard/runs/pending-register`,
    { headers: { 'x-api-key': apiKey } },
  );
  const json = await resp.json().catch(() => ({} as any));
  if (!resp.ok) throw new Error(json?.message || `HTTP ${resp.status}`);
  return (json.runs ?? []) as OnboardRun[];
}

// ── Stage-5 PR actions ────────────────────────────────────────────
// Backed by backend/product/src/modules/onboard/pr-actions.ts. All
// three are no-arg POSTs (the PR number is in the path) returning
// `{ ok, result }`. They surface GitHub errors verbatim via thrown
// Error so the row's mutation panel renders them inline.

/** Squash-merge the PR via GitHub. */
export async function mergeOnboardPr(prNumber: number): Promise<void> {
  await prActionFetch(`/v1/observe/onboard/pulls/${prNumber}/merge`, 'merge_failed');
}

/** Close (without merging) the PR. */
export async function closeOnboardPr(prNumber: number): Promise<void> {
  await prActionFetch(`/v1/observe/onboard/pulls/${prNumber}/close`, 'close_failed');
}

/** Re-run any failed workflow runs on the PR's current head SHA.
 *  Returns the number of workflow runs the rerun was actually fired
 *  on (0 means nothing was failing). */
export async function rerunOnboardPrCi(prNumber: number): Promise<{ reran: number }> {
  const json = await prActionFetch(`/v1/observe/onboard/pulls/${prNumber}/rerun-ci`, 'rerun_failed');
  const r = (json as any).result ?? {};
  return { reran: Number(r.reran ?? 0) };
}

async function prActionFetch(path: string, errorTag: string): Promise<unknown> {
  const apiKey = getApiKey() ?? '';
  const base   = getBaseUrl();
  // Intentionally NO content-type header — these POSTs carry no body
  // and Fastify's default JSON parser rejects an empty body with
  // `content-type: application/json` (HTTP 400, "Body cannot be empty
  // when content-type is set…"). Omitting the header makes Fastify
  // treat it as a no-body request and skip parsing.
  const resp   = await fetch(`${base}${path}`, {
    method:  'POST',
    headers: { 'x-api-key': apiKey },
  });
  const json: any = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(json?.message || `${errorTag}: HTTP ${resp.status}`);
  return json;
}

/** Tiny shared POST helper — keeps the create call free of boilerplate. */
async function jsonPost<T>(path: string, body: unknown, errorTag: string): Promise<T> {
  const apiKey = getApiKey() ?? '';
  const base   = getBaseUrl();
  const resp   = await fetch(`${base}${path}`, {
    method:  'POST',
    headers: {
      'content-type': 'application/json',
      'x-api-key':    apiKey,
    },
    body: JSON.stringify(body),
  });
  const json = await resp.json().catch(() => ({} as any));
  if (!resp.ok) {
    throw new Error(json?.message || `${errorTag}: HTTP ${resp.status}`);
  }
  // Server wraps single-doc responses in `{ ok, run }` — unwrap.
  return (json.run ?? json) as T;
}
